import { html } from 'lit/html.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { inputMask, dispatchEvent } from '@fil-global/shared';
import { TextField, STATUS } from '@fil-global/text-field';
// Replace fil-icon with specific icons as fil-icon will import all icons into this components and cause poor performance of it.
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-view-1'; // eye open
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-view-off'; // eye off
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-alert-circle'; // invalid
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-custom-check-circle-1'; // valid
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-search';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-add'; // valid

import componentStyles from './fil-text-input.styles.js';
import { TYPE } from './util.js';

const ifNotEmpty = value => ifDefined(value === '' ? undefined : (value ?? undefined));

/**
 * @tag fil-text-input
 * @property {'primary'|'secondary'} [appearance=primary] Appearance of the component
 * @property {string} [assistiveText] Assistive text to be used in the component
 * @property {string}[autocomplete] HTML autocomplete property of the component
 * @property {boolean}[autofocus] HTML autofocus property of the component
 * @property {boolean}[characterCount] Whether to show character count in the component
 * @property {string}[currency] Currency to be used in case of currency mask in the component
 * @property {boolean}[disabled=false] HTML disabled attribute of the component
 * @property {string}[icon] Icon to be used in the component
 * @property {string}[inputmode] HTML inputmode property of the component
 * @property {string} [label] Label of the component
 * @property {"1-col"|"2-cols"} [layout=1-col] Layout of the component
 * @property {"currency"|"dob"|"number"|"nationalInsuranceNumber"|"percentage"} [mask]
 * @property {number}[max] HTML max property of the component
 * @property {number}[maxlength] HTML maxlength property of the component
 * @property {number}[min] HTML min property of the component
 * @property {boolean}[obvious=false] Password hint position of the component
 * @property {array}[passwordHint] List of password hints to be shown
 * @property {string}[pattern] HTML pattern property of the component
 * @property {string}[placeholder] HTML placeholder property of the component
 * @property {boolean}[readonly] HTML readonly property of the component
 * @property {boolean}[required] HTML required property of the component
 * @property {'standard'|'small'}[size] Size of the component
 * @property {"disabled"|"invalid"|"valid"} [status]
 * @property {'number'|'password'|'text'}[type=text] Type of the component
 * @property {number}[step] HTML step property of the component
 * @property {boolean}[spellcheck=false] HTML spellcheck property of the component
 * @property {'left'|'right'}[textAlign] Text align property of the component
 * @property {string}[tooltip]
 * @property {string}[tooltipExtraProps]
 * @fires blur.gds.input (event: Event) => void
 * @fires blur.gds.input (event: Event) => void
 * @fires keyup.gds.input (event: Event) => void
 * @fires change.gds.input (event: Event) => void
 * @fires input.gds.input (event: Event) => void
 * @fires click.gds.input (event: Event) => void
 */
export class FilTextInput extends TextField {
  static properties = {
    autocomplete: { type: String },
    autofocus: { type: Boolean },
    characterCount: { type: Boolean },
    currency: { type: String },
    icon: { type: String },
    inputmode: { type: String },
    mask: { type: String }, // {"number" | "dob" | "currency" | "percentage" | "nationalInsuranceNumber"}
    max: { type: Number },
    maxlength: { type: Number },
    min: { type: Number },
    obvious: { type: Boolean }, // passwords hints position is fixed or pop-up
    passwordHints: { type: Array },
    pattern: { type: String },
    placeholder: { type: String },
    readonly: { type: Boolean },
    required: { type: Boolean },
    size: { type: String }, // 'standard' | 'small'
    spellcheck: { type: Boolean },
    step: { type: Number },
    textAlign: { type: String },
    type: { type: String }, // {"text" | "date" | "password"}
    _compositionLock: { type: Boolean, state: true },
  };

  static styles = [super.styles, componentStyles];

  constructor() {
    super();
    this.autofocus = false;
    this.characterCount = false;
    this.inputmode = 'text';
    this.layout = '1-col';
    this.min = this.type === TYPE.NUMBER ? this.min : undefined;
    this.max = this.type === TYPE.NUMBER ? this.max : undefined;
    this.maxlength = 100;
    this.obvious = false;
    this.pattern = null;
    this.readonly = false;
    this.required = false;
    this.size = 'standard';
    this.spellcheck = false;
    this.textAlign = 'left';
    this.type = TYPE.TEXT;
    this.currency = this.mask === 'currency' ? '£ ' : '';
  }

  handleIconClick(e) {
    // Prevent the input from losing focus
    e.preventDefault();
    dispatchEvent(this, 'click.gds.input', { target: 'icon' });
    if (this.type === TYPE.PASSWORD || (this.passwordHints && this.passwordHints.length)) {
      this.toggleIcon();
    }
  }

  handleIconKeydown(e) {
    if (e.key === 'Enter' || e.code === 'Space' || e.key === ' ') {
      e.preventDefault();
      dispatchEvent(this, 'keydown.gds.input', { target: 'icon' });
      if (this.type === TYPE.PASSWORD || (this.passwordHints && this.passwordHints.length)) {
        this.toggleIcon();
      }
      setTimeout(() => {
        let icon = this.shadowRoot.querySelector(
          '.fil-text-field__icon.fil-text-field__icon--normal',
        );
        icon.focus();
      }, 0);
    }
  }

  handleSpecificIcons() {
    const { icon, size } = this;
    if (!icon) return null;
    const className = 'fil-text-field__icon fil-text-field__icon--normal';
    const iconsize = size === 'small' ? 16 : 20;
    switch (icon) {
      case 'view-1':
        return html`<fil-icon-view-1
          label="eye open icon"
          size=${iconsize}
          class=${className}
          @mousedown=${this.handleIconClick}
          @keydown=${this.handleIconKeydown}
          tabindex="0"
        ></fil-icon-view-1>`;
      case 'view-off':
        return html`<fil-icon-view-off
          label="eye off icon"
          size=${iconsize}
          class=${className}
          @mousedown=${this.handleIconClick}
          @keydown=${this.handleIconKeydown}
          tabindex="0"
        ></fil-icon-view-off>`;
      case 'search':
        return html`<fil-icon-search
          label="eye open icon"
          size=${iconsize}
          class=${className}
          @mousedown=${this.handleIconClick}
        ></fil-icon-search>`;
      case 'add':
        return html`<fil-icon-add
          label="eye open icon"
          size=${iconsize}
          class=${className}
          @mousedown=${this.handleIconClick}
        ></fil-icon-add>`;
      default:
        /* eslint-disable no-console*/
        console.warn('Custom icons not supported. Please contact GDS team for assistance');
        return null;
    }
  }

  handleValidationIcons() {
    const { status, size } = this;
    if (!status) return null;
    const className = 'fil-text-field__icon';
    const iconSize = size === 'small' ? 16 : 20;
    if (status === STATUS.VALID) {
      return html`
        <fil-icon-custom-check-circle-1
          label="valid icon"
          size=${iconSize}
          filled
          class=${className}
          color="green-600"
        ></fil-icon-custom-check-circle-1>
      `;
    } else if (status === STATUS.INVALID) {
      return html`
        <fil-icon-custom-alert-circle
          label="invalid icon"
          size=${iconSize}
          filled
          class=${className}
          color="brand-error-red"
        ></fil-icon-custom-alert-circle>
      `;
    } else {
      /* eslint-disable no-console*/
      console.warn('Only valid and invalid icons are support at present.');
      return null;
    }
  }

  iconTemplate() {
    const { icon, status } = this;
    if (status || icon) {
      return html`${this.handleSpecificIcons()}${this.handleValidationIcons()} `;
    }
    return null;
  }

  toggleIcon() {
    if (this.icon === 'view-1') {
      this.icon = 'view-off';
      this.altType = TYPE.TEXT;
    } else {
      this.icon = 'view-1';
      this.altType = TYPE.PASSWORD;
    }
  }

  updated(changedProperties) {
    super.updated();
    const el = this.shadowRoot.querySelector('input');

    if (changedProperties.has('mask') && this.mask) {
      inputMask(el, this.mask, this.mask === 'currency' ? this.currency : '');
    }
  }

  characterCountTemplate() {
    if (this.characterCount) {
      const value = this.value ? this.value : '';
      return html`<p class="fil-text-input__character-count" aria-hidden="true">
        ${value.length}/${this.maxlength}
      </p>`;
    }
    return null;
  }

  passwordHintTemplate() {
    if (this.passwordHints) {
      const passwordHintsPopUp = this.type === TYPE.PASSWORD && !this.obvious;
      const classes = {
        'fil-password-hint': true,
        'fil-password-hint--enter': passwordHintsPopUp,
        'fil-password-hint--create': this.obvious,
      };
      return html`<div class=${classMap(classes)}>
        ${passwordHintsPopUp
          ? html`<p class="fil-password-hint__intro">The password must include:</p>`
          : ''}
        <ul class="fil-password-hint__list">
          ${this.passwordHints.map(passwordHint => {
            const pwdClasses = {
              'fil-password-hint__item': true,
              'fil-password-hint__item--active': passwordHint.active,
            };
            return html`<li class=${classMap(pwdClasses)}>${passwordHint.label}</il>`;
          })}
        </ul>
      </div>`;
    }
    return null;
  }

  handleKeyUp(e) {
    const { value } = e.target;
    this.value = value;
    dispatchEvent(this, 'keyup.gds.input', { value });
  }

  changePasswordStatus() {
    const { type, status, passwordHints } = this;
    if (type === TYPE.PASSWORD) {
      if (passwordHints && Array.isArray(passwordHints) && passwordHints.length > 0) {
        let isValid = status === STATUS.VALID;
        let passedConditions = 0;
        passwordHints.forEach(passwordHint => {
          if (!passwordHint.active) {
            isValid = false;
          } else {
            passedConditions += 1;
          }
        });

        if (passwordHints.length === passedConditions) {
          isValid = true;
        }
        this.status = isValid;
        return isValid;
      }
    }
    return status;
  }

  handleChange(e) {
    const status = this.changePasswordStatus();
    const { value } = e.target;
    this.value = value;
    dispatchEvent(this, 'change.gds.input', {
      value,
      status,
    });
  }

  handleFocus(e) {
    const status = this.changePasswordStatus();
    dispatchEvent(this, 'focus.gds.input', { value: e.target.value, status });
  }

  handleBlur(e) {
    const status = this.changePasswordStatus();
    dispatchEvent(this, 'blur.gds.input', { value: e.target.value, status });
  }

  handleCompositionStart() {
    this._compositionLock = true;
  }

  handleCompositionEnd(e) {
    this._compositionLock = false;
    this.handleInput(e);
  }

  handleInput(e) {
    if (!this._compositionLock) {
      const { value } = e.target;
      this.value = value;

      // handle password validation
      if (this.passwordHints) {
        this.passwordHints = this.passwordHints.map(passwordHint => {
          const regex = RegExp(passwordHint.regex);
          return {
            ...passwordHint,
            active: regex.test(value),
          };
        });
      }

      const status = this.changePasswordStatus();
      dispatchEvent(this, 'input.gds.input', { value, status });
    }
  }

  renderElement() {
    const disabled = this.disabled || this.status === STATUS.DISABLED;
    const classes = {
      [`fil-text-input--${this.appearance}`]: this.appearance !== 'primary',
      [`fil-text-input--${this.size}`]: this.size !== 'standard',
      'fil-text-input--valid': this.status === STATUS.VALID,
      'fil-text-input--invalid': this.status === STATUS.INVALID,
      'fil-text-input--disabled': disabled,
      [`fil-text-input--${this.textAlign}`]: !!this.textAlign,
      'fil-text-input--icon': this.icon && this.textAlign === 'left',
    };
    return html`
      <input
        aria-readonly=${this.readonly}
        aria-required=${this.required}
        autocomplete="${ifDefined(this.autocomplete)}"
        inputmode="${this.inputmode}"
        ?autofocus="${this.autofocus}"
        class="fil-text-input${classMap(classes)}"
        ?disabled=${disabled}
        id=${this.elementId}
        .max=${ifDefined(this.max)}
        maxlength=${this.maxlength}
        .min=${ifDefined(this.min)}
        name=${this.name}
        pattern="${ifNotEmpty(this.pattern)}"
        placeholder="${ifNotEmpty(this.placeholder)}"
        ?readonly=${this.readonly}
        ?required=${this.required}
        ?spellcheck=${this.spellcheck}
        type=${this.altType ? this.altType : this.type}
        title="${this.label} input"
        aria-label="${this.label} input"
        .value=${ifDefined(this.value)}
        .step=${ifDefined(this.step)}
        @keyup=${this.handleKeyUp}
        @change=${this.handleChange}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
        @input=${this.handleInput}
        @compositionstart=${this.handleCompositionStart}
        @compositionend=${this.handleCompositionEnd}
      />${this.passwordHintTemplate()} ${this.iconTemplate()} ${this.characterCountTemplate()}
    `;
  }
}

export default FilTextInput;
